import { message } from 'antd';
import { useCallback, useState } from 'react';

import { type EventJobEventResponseDTO, waitForEventJob } from '@/api/events/api';
import {
  createKnowledgeNote,
  startProjectPdfDocumentJob,
  startProjectPowerPointDocumentJob,
  startProjectTabularDocumentJob,
  startProjectWordDocumentJob,
} from '@/api/knowledge/api';
import { isJwtExpiredOrExpiringSoon } from '@/utils/jwt';
import { generateUuidV4, isValidUuid } from '@/utils/uuid';

type SummaryStyle = 'concise' | 'detailed';

export interface SaveInlineNoteParams {
  noteTitle: string;
  noteDraftId: string;
  noteTags: string[];
  latestMarkdown: string;
  projectId?: string;
}

export interface UseAddAssetResult {
  uploading: boolean;
  uploadPhase: string | null;
  uploadProgress: number | null;
  uploadDocument: (
    file: File,
    summaryStyle: SummaryStyle,
    onWordDocumentUploaded: (cardId: string) => void,
    onActionComplete?: () => void,
  ) => Promise<void>;
  saveInlineNote: (
    params: SaveInlineNoteParams,
    onInlineNoteSaved?: (cardId: string) => void,
    onActionComplete?: () => void,
  ) => Promise<void>;
}

/** Selects the correct API upload function based on file extension. */
function resolveUploader(fileName: string) {
  const lower = fileName.toLowerCase();
  if (lower.endsWith('.pdf')) {
    return startProjectPdfDocumentJob;
  }
  if (lower.endsWith('.pptx')) {
    return startProjectPowerPointDocumentJob;
  }
  if (lower.endsWith('.docx')) {
    return startProjectWordDocumentJob;
  }
  return startProjectTabularDocumentJob;
}

function normalizeUploadErrorMessage(error: unknown): string {
  const msg = error instanceof Error ? error.message : 'Upload failed';
  if (msg.toLowerCase().includes('pdf has no extractable text')) {
    return (
      'This PDF has no extractable text. OCR is not supported yet. ' +
      'Export a text-based PDF and try again.'
    );
  }
  return msg;
}

/**
 * Manages the async document-upload and inline-note-save workflows for the Add Asset form.
 * Form state (intent, linkUrl, fileList, note fields) stays in the component.
 */
export function useAddAsset(
  accessToken: string | null,
  clientId: string | undefined,
  projectId: string | undefined,
  microsoftOboAssertion: string | null,
  onMicrosoftRefreshObo: (() => Promise<string | null>) | undefined,
  onMicrosoftReauth: (() => Promise<void> | void) | undefined,
): UseAddAssetResult {
  const [uploading, setUploading] = useState(false);
  const [uploadPhase, setUploadPhase] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);

  /** Returns a fresh Microsoft OBO assertion for the upload call, triggering re-auth if expired. */
  const resolveMicrosoftOboAssertionForUpload = useCallback(async (): Promise<string | null> => {
    const oboForUpload: string | null = microsoftOboAssertion;
    if (oboForUpload && !isJwtExpiredOrExpiringSoon(oboForUpload)) {
      return oboForUpload;
    }
    const refreshed = await onMicrosoftRefreshObo?.();
    if (!refreshed) {
      void message.info('Microsoft token expired. Please sign in again to reconnect Microsoft.');
      await onMicrosoftReauth?.();
      return null;
    }
    return refreshed;
  }, [microsoftOboAssertion, onMicrosoftRefreshObo, onMicrosoftReauth]);

  /** Uploads the staged document file, polls the async job, then notifies the parent. */
  const uploadDocument = useCallback(
    async (
      file: File,
      summaryStyle: SummaryStyle,
      onWordDocumentUploaded: (cardId: string) => void,
      onActionComplete?: () => void,
    ): Promise<void> => {
      if (!accessToken || !clientId || !projectId) {
        void message.error('Missing session context. Please refresh and try again.');
        return;
      }
      setUploading(true);
      setUploadPhase('knowledge.upload_received');
      setUploadProgress(0);
      try {
        const oboForUpload = await resolveMicrosoftOboAssertionForUpload();
        if (oboForUpload === null) {
          return;
        }
        const sessionId = generateUuidV4();
        const uploader = resolveUploader(file.name);
        const started = await uploader(
          accessToken,
          clientId,
          projectId,
          file,
          summaryStyle,
          oboForUpload,
          sessionId,
        );
        const onEvent = (event: EventJobEventResponseDTO): void => {
          if (event.phase) {
            setUploadPhase(event.phase);
          }
          if (event.progress !== null && event.progress !== undefined) {
            setUploadProgress(event.progress);
          }
        };
        const snapshot = await waitForEventJob<{ card_id?: string }>(accessToken, started, onEvent);
        const cardId = snapshot.result?.card_id;
        if (!cardId) {
          throw new Error('Document upload completed without a card id.');
        }
        setUploadPhase('knowledge.completed');
        setUploadProgress(100);
        void message.success('Document uploaded and indexed');
        onActionComplete?.();
        onWordDocumentUploaded(cardId);
      } catch (error) {
        const msg = normalizeUploadErrorMessage(error);
        void message.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [accessToken, clientId, projectId, resolveMicrosoftOboAssertionForUpload],
  );

  /** Saves the inline note draft to the API and notifies the parent with the returned card ID. */
  const saveInlineNote = useCallback(
    async (
      params: SaveInlineNoteParams,
      onInlineNoteSaved?: (cardId: string) => void,
      onActionComplete?: () => void,
    ): Promise<void> => {
      if (!accessToken || !clientId) {
        void message.error('Missing session context. Please refresh and try again.');
        return;
      }
      const { noteTitle, noteDraftId, noteTags, latestMarkdown, projectId: noteProjectId } = params;
      if (!noteTitle.trim() || !latestMarkdown.trim()) {
        return;
      }
      const draftId = noteDraftId.trim();
      if (!isValidUuid(draftId)) {
        void message.error('Invalid draft id. Please cancel and try again.');
        return;
      }
      setUploading(true);
      try {
        const response = await createKnowledgeNote(accessToken, clientId, {
          project_id: noteProjectId ?? null,
          card_id: draftId,
          title: noteTitle.trim(),
          markdown_content: latestMarkdown.trim(),
          tags: noteTags,
        });
        void message.success('Note saved to project');
        onInlineNoteSaved?.(response.card_id);
        onActionComplete?.();
      } catch (error) {
        const msg = error instanceof Error ? error.message : 'Unable to save note';
        void message.error(msg);
      } finally {
        setUploading(false);
      }
    },
    [accessToken, clientId],
  );

  return { uploading, uploadPhase, uploadProgress, uploadDocument, saveInlineNote };
}
